import useSWR from 'swr'
import axios from '@/lib/axios'
import { useEffect } from 'react'
import { useRouter } from 'next/navigation'

interface AuthProps {
  middleware?: 'auth' | 'guest'
  redirectIfAuthenticated?: string
}

export const useAuth = ({ middleware, redirectIfAuthenticated }: AuthProps = {}) => {
  const router = useRouter()

  const { data: user, error, mutate, isLoading } = useSWR('/api/user', () =>
    axios
      .get('/api/user')
      .then(res => res.data.data)
      .catch(error => {
        if (error.response.status !== 409) throw error

        router.push('/api/verify-email')
      }),
  )

  const csrf = () => axios.get('/sanctum/csrf-cookie')

  const register = async ({ setErrors, onSuccess, ...props }: {
    setErrors: (errors: any) => void,
    onSuccess: (data: any) => void
    [key: string]: any
  }) => {
    await csrf()

    setErrors([])

    axios
      .post('/api/register', props)
      .then((res: any) => {
        onSuccess(res.data)
        mutate()
      })
      .catch(error => {
        if (error.response.status !== 422) throw error

        setErrors(error.response.data.errors)
      })
  }

  const verify = async ({ onError, onSuccess, ...props }: {
    onError: (data: any) => void
    [key: string]: any
  }) => {
    await csrf()

    axios
      .post('/api/verify', props)
      .then((res: any) => {
        onSuccess(res.data)
        mutate()
      })
      .catch(error => {
        onError(error.response.data);
      })
  }

  const resend = async ({ setErrors, onSuccess, ...props }: {
    onSuccess: (data: any) => void
    setErrors: (errors: any) => void
    [key: string]: any
  }) => {
    await csrf()

    setErrors([])

    axios
      .post('/api/resend', props)
      .then((res: any) => {
        onSuccess(res.data);
        mutate();
      })
      .catch(error => {
        if (error.response.status !== 422) throw error

        setErrors(error.response.data.errors)
      })
  }

  const login = async ({ setErrors, onSuccess, onError, setStatus, ...props }: {
    setErrors: (errors: any) => void
    setStatus: (status: any) => void
    onSuccess: (data: any) => void
    onError: () => void
    [key: string]: any
  }) => {
    await csrf()

    setErrors([])
    setStatus(null)

    axios
      .post('/api/login', props)
      .then((res: any) => {
        onSuccess(res.data)
        mutate()
      })
      .catch(error => {
        if (error.response.status !== 422) throw error

        setErrors(error.response.data.errors)
        onError();
      })
  }

  const loginPassword = async ({ setErrors, onSuccess, onUnauthorized, setStatus, ...props }: {
    setErrors: (errors: any) => void
    setStatus: (status: any) => void
    onSuccess: (data: any) => void
    onUnauthorized: (message?: string) => void
    [key: string]: any
  }) => {
    await csrf()

    setErrors([])
    setStatus(null)

    axios
      .post('/api/login-password', props)
      .then((res: any) => {
        onSuccess(res.data)
        mutate()
      })
      .catch(error => {
        if (error.response.status === 401) {
          onUnauthorized(error.response.data?.message)
          return
        }

        if (error.response.status !== 422) throw error

        setErrors(error.response.data.errors)
      })
  }

  const logout = async () => {
    if (!error) {
      await axios.post('/api/logout').then(() => mutate())
    }

    window.location.pathname = '/login'
  }

  useEffect(() => {
    if (middleware === 'guest' && redirectIfAuthenticated && user) {
      router.push(redirectIfAuthenticated)
    }

    if (middleware === 'auth' && error) {
      logout()
    }
  }, [user, error])

  return {
    isLoading,
    user,
    register,
    login,
    loginPassword,
    verify,
    resend,
    logout
  }
}